import type {
  CostAllowanceInput,
  LoadedDeveloperCostBasis,
  Scenario,
} from "./types";

export interface ResolvedCostAllowance {
  mode: "amount" | "rate";
  amount: number;
  rate: number;
}

export type ScopeClassification =
  "embedded" | "separately_added" | "excluded_tbc" | "not_applicable";

export interface CostScopeRegisterItem {
  id: string;
  label: string;
  classification: ScopeClassification;
  mapping: string;
  note: string;
}

export const KURRABA_SOURCE_ID = "kurraba-dev-table-2026-07-20";

export const KURRABA_LOADED_COST_BASIS: LoadedDeveloperCostBasis = {
  mode: "loaded_aggregate",
  sourceId: KURRABA_SOURCE_ID,
  sourceName:
    "Kurraba (source owner) — Development cost schedule; attachment Dev Table_Kurraba 260720.xlsx",
  sourceDate: "2026-07-20",
  buildingWorks: 39_549_620.72,
  siteWorks: 4_509_327.28,
  sourceBuildingAreaSqm: 13_482,
  embeddedPreliminariesRate: 0.15,
  embeddedBuilderMarginRate: 0.03,
  embeddedBuilderOverheadsRate: 0.03,
  upperBoundBuildingWorks: 45_290_585,
  upperBoundSiteWorks: 7_436_315,
  upperBoundTotal: 52_726_899.32,
  gstStatus: "unverified",
  escalationStatus: "included",
  allocationMethod: "scenario_gfa_pro_rata",
};

export const KURRABA_COST_BRIDGE = {
  adoptedTotal:
    KURRABA_LOADED_COST_BASIS.buildingWorks +
    KURRABA_LOADED_COST_BASIS.siteWorks,
  buildingVariance:
    KURRABA_LOADED_COST_BASIS.upperBoundBuildingWorks -
    KURRABA_LOADED_COST_BASIS.buildingWorks,
  siteVariance:
    KURRABA_LOADED_COST_BASIS.upperBoundSiteWorks -
    KURRABA_LOADED_COST_BASIS.siteWorks,
  roundingReconciliation:
    KURRABA_LOADED_COST_BASIS.upperBoundTotal -
    KURRABA_LOADED_COST_BASIS.upperBoundBuildingWorks -
    KURRABA_LOADED_COST_BASIS.upperBoundSiteWorks,
  totalVariance:
    KURRABA_LOADED_COST_BASIS.upperBoundTotal -
    (KURRABA_LOADED_COST_BASIS.buildingWorks +
      KURRABA_LOADED_COST_BASIS.siteWorks),
  upperBoundTotal: KURRABA_LOADED_COST_BASIS.upperBoundTotal,
} as const;

export const KURRABA_SCOPE_REGISTER: readonly CostScopeRegisterItem[] = [
  {
    id: "school-buildings",
    label: "School building works",
    classification: "embedded",
    mapping: "Loaded building works",
    note: "Fixed source total; scenario GFA allocation is for phasing only.",
  },
  {
    id: "vt",
    label: "Vertical transport (VT)",
    classification: "embedded",
    mapping: "Loaded building works",
    note: "Provisionally embedded; confirm in detailed QS reconciliation.",
  },
  {
    id: "pitch",
    label: "Football pitch",
    classification: "embedded",
    mapping: "Loaded site/outdoor works",
    note: "No separate amenity construction addition in loaded mode.",
  },
  {
    id: "external-works",
    label: "External works",
    classification: "embedded",
    mapping: "Loaded site/outdoor works",
    note: "Whole-of-site outdoor works pool.",
  },
  {
    id: "parking",
    label: "Parking construction",
    classification: "embedded",
    mapping: "Loaded site/outdoor works",
    note: "Provisionally embedded to prevent double counting.",
  },
  {
    id: "covered-play",
    label: "Covered play construction",
    classification: "embedded",
    mapping: "Loaded site/outdoor works",
    note: "Provisionally embedded to prevent double counting.",
  },
  {
    id: "operator-fitout",
    label: "Operator fitout",
    classification: "separately_added",
    mapping: "Operator capex",
    note: "Does not reduce or duplicate developer loaded works.",
  },
  {
    id: "signage",
    label: "Signage",
    classification: "excluded_tbc",
    mapping: "No model cost",
    note: "Source quote records signage as TBC.",
  },
] as const;

export function resolveCostAllowance(
  input: CostAllowanceInput | undefined,
  fallbackRate: number,
  base: number,
): ResolvedCostAllowance {
  const safeBase = Number.isFinite(base) && base > 0 ? base : 0;
  if (
    input?.mode === "amount" &&
    Number.isFinite(input.amount) &&
    input.amount >= 0
  ) {
    return {
      mode: "amount",
      amount: input.amount,
      rate: safeBase > 0 ? input.amount / safeBase : 0,
    };
  }
  const candidateRate =
    input?.mode === "rate" && Number.isFinite(input.rate)
      ? input.rate
      : fallbackRate;
  const rate =
    Number.isFinite(candidateRate) && candidateRate >= 0 ? candidateRate : 0;
  return { mode: "rate", amount: safeBase * rate, rate };
}

export function applyKurrabaCostPreset(source: Scenario): Scenario {
  const scenario = structuredClone(source);
  scenario.assumptions.developerCostBasis = structuredClone(
    KURRABA_LOADED_COST_BASIS,
  );
  scenario.assumptions.developerCostAllowances = {
    contingency: { mode: "amount", amount: 6_271_628 },
    professionalFees: { mode: "amount", amount: 4_546_078 },
    developmentFee: { mode: "amount", amount: 2_744_021 },
    consentsAmount: 2_065_197,
  };
  scenario.assumptions.constructionLvrRate = 0.675;
  scenario.assumptions.reddamCapexContribution = 10_000_000;
  scenario.assumptions.contributionTiming = "progressive";
  return scenario;
}

// ---------------------------------------------------------------------------
// Versatile RevG order of cost (builder evidence, 20 Aug 2026)
//
// Source: `Reddam Waterloo Rd Order of cost RevG_20.8.26.xlsx` (Versatile).
// SHA-256 50bf7f0b39de8d2010dc7b93726634e70070e561413b502c730cda5d06465bda.
// Custody: Downloads/Spreadsheets and the Codex controlled baseline
// `.planning/waterloo-revg-controlled-baseline-2026-08-26/` (commit 1ec5554).
// Reconciliation note: docs/scenarios/2026-09-08-mac-park-cost-source-reconciliation-revg.md.
//
// The workbook's `Order of Cost` sheet reads six package totals from
// `Trade Summary!D14:D19` (the upper block D3:D12 is a legacy `#REF!` block and
// is never used). Prelims (10%), overhead (3%) and margin are flat percentages
// of the raw construction total, so the loaded building / site split is an
// exact pro-rata of the raw split. The workbook's margin cell computes 4% while
// its header states 5%; Kyle (2026-09-08) directed 5% across the board, so the
// adopted basis carries the stated 5% and the 4% submitted total is kept only
// as the source figure (`VERSATILE_REVG_RAW_BASIS.submittedTotal`). Consultant fees
// (8%) and contingency (3%) are header columns whose cells are blank in the
// submitted total; the preset carries them as amount-mode allowances on the
// raw construction base. Roof upgrades and mechanical replacement sit outside
// the submitted total and are retained only in the upper bound (the Codex 26 Aug
// fail-closed gross contractor basis, also at 5%).
// ---------------------------------------------------------------------------
export const VERSATILE_REVG_SOURCE_ID = "versatile-revg-2026-08-20";

/** Raw trade / construction totals by package (`Trade Summary!D14:D19`). */
export const VERSATILE_REVG_RAW_TRADE = {
  juniorMiddleSchool: 19_477_824.88079125,
  seniorSchool: 8_767_191.869124468,
  warehouse: 6_854_212.068035113,
  multiPurposeBasketball: 9_442_204.523664959,
  externalSportsFields: 1_061_675,
  externalWorks: 5_224_450,
} as const;

/** Loadings applied to the raw construction total (`Order of Cost!D3, G3, H3`). */
export const VERSATILE_REVG_LOADINGS = {
  preliminariesRate: 0.1,
  overheadRate: 0.03,
  marginRateFormula: 0.04,
  marginRateStated: 0.05,
  consultantFeesRate: 0.08,
  contingencyRate: 0.03,
} as const;

/** Allowances shown outside the submitted total (`Order of Cost!C16:C17`). */
export const VERSATILE_REVG_OUTSIDE_TOTAL = {
  roofUpgrades: 1_347_145,
  mechanicalReplacement: 1_950_000,
  optionalAvCabling: 363_000,
} as const;

const REVG_RAW_BUILDING =
  VERSATILE_REVG_RAW_TRADE.juniorMiddleSchool +
  VERSATILE_REVG_RAW_TRADE.seniorSchool +
  VERSATILE_REVG_RAW_TRADE.warehouse +
  VERSATILE_REVG_RAW_TRADE.multiPurposeBasketball;
const REVG_RAW_SITE =
  VERSATILE_REVG_RAW_TRADE.externalSportsFields +
  VERSATILE_REVG_RAW_TRADE.externalWorks;
const REVG_RAW_TOTAL = REVG_RAW_BUILDING + REVG_RAW_SITE;
// The workbook's own headline (4% formula margin) — a source figure, not adopted.
const REVG_SUBMITTED_LOADING =
  1 +
  VERSATILE_REVG_LOADINGS.preliminariesRate +
  VERSATILE_REVG_LOADINGS.overheadRate +
  VERSATILE_REVG_LOADINGS.marginRateFormula;
// Adopted and upper-bound loading: 5% across the board (Kyle, 2026-09-08).
const REVG_ADOPTED_LOADING =
  1 +
  VERSATILE_REVG_LOADINGS.preliminariesRate +
  VERSATILE_REVG_LOADINGS.overheadRate +
  VERSATILE_REVG_LOADINGS.marginRateStated;
const REVG_RETAINED_OUTSIDE =
  VERSATILE_REVG_OUTSIDE_TOTAL.roofUpgrades +
  VERSATILE_REVG_OUTSIDE_TOTAL.mechanicalReplacement;

/** Raw-basis figures the reconciliation note and tests pin against the source. */
export const VERSATILE_REVG_RAW_BASIS = {
  rawBuilding: REVG_RAW_BUILDING,
  rawSite: REVG_RAW_SITE,
  rawTotal: REVG_RAW_TOTAL,
  submittedLoading: REVG_SUBMITTED_LOADING,
  submittedTotal: REVG_RAW_TOTAL * REVG_SUBMITTED_LOADING,
  adoptedLoading: REVG_ADOPTED_LOADING,
  retainedOutsideTotal: REVG_RETAINED_OUTSIDE,
  grossWorksRetainingOutside: REVG_RAW_TOTAL + REVG_RETAINED_OUTSIDE,
} as const;

export const VERSATILE_REVG_LOADED_COST_BASIS: LoadedDeveloperCostBasis = {
  mode: "loaded_aggregate",
  sourceId: VERSATILE_REVG_SOURCE_ID,
  sourceName:
    "Versatile (builder) — Reddam Waterloo Rd Order of cost RevG_20.8.26.xlsx",
  sourceDate: "2026-08-20",
  buildingWorks: REVG_RAW_BUILDING * REVG_ADOPTED_LOADING,
  siteWorks: REVG_RAW_SITE * REVG_ADOPTED_LOADING,
  sourceBuildingAreaSqm: 13_593,
  embeddedPreliminariesRate: VERSATILE_REVG_LOADINGS.preliminariesRate,
  embeddedBuilderMarginRate: VERSATILE_REVG_LOADINGS.marginRateStated,
  embeddedBuilderOverheadsRate: VERSATILE_REVG_LOADINGS.overheadRate,
  upperBoundBuildingWorks:
    (REVG_RAW_BUILDING + REVG_RETAINED_OUTSIDE) * REVG_ADOPTED_LOADING,
  upperBoundSiteWorks: REVG_RAW_SITE * REVG_ADOPTED_LOADING,
  upperBoundTotal:
    (REVG_RAW_TOTAL + REVG_RETAINED_OUTSIDE) * REVG_ADOPTED_LOADING,
  gstStatus: "unverified",
  escalationStatus: "unverified",
  allocationMethod: "scenario_gfa_pro_rata",
};

export const VERSATILE_REVG_COST_BRIDGE = {
  adoptedTotal:
    VERSATILE_REVG_LOADED_COST_BASIS.buildingWorks +
    VERSATILE_REVG_LOADED_COST_BASIS.siteWorks,
  buildingVariance:
    VERSATILE_REVG_LOADED_COST_BASIS.upperBoundBuildingWorks -
    VERSATILE_REVG_LOADED_COST_BASIS.buildingWorks,
  siteVariance:
    VERSATILE_REVG_LOADED_COST_BASIS.upperBoundSiteWorks -
    VERSATILE_REVG_LOADED_COST_BASIS.siteWorks,
  roundingReconciliation:
    VERSATILE_REVG_LOADED_COST_BASIS.upperBoundTotal -
    VERSATILE_REVG_LOADED_COST_BASIS.upperBoundBuildingWorks -
    VERSATILE_REVG_LOADED_COST_BASIS.upperBoundSiteWorks,
  totalVariance:
    VERSATILE_REVG_LOADED_COST_BASIS.upperBoundTotal -
    (VERSATILE_REVG_LOADED_COST_BASIS.buildingWorks +
      VERSATILE_REVG_LOADED_COST_BASIS.siteWorks),
  upperBoundTotal: VERSATILE_REVG_LOADED_COST_BASIS.upperBoundTotal,
} as const;

export const VERSATILE_REVG_SCOPE_REGISTER: readonly CostScopeRegisterItem[] = [
  {
    id: "school-buildings",
    label: "School building works",
    classification: "embedded",
    mapping: "Loaded building works",
    note: "Four RevG packages (Junior & Middle, Senior, Warehouse, Multi-purpose / Basketball) at the submitted loadings; scenario GFA allocation is for phasing only.",
  },
  {
    id: "vt",
    label: "Vertical transport (VT)",
    classification: "embedded",
    mapping: "Loaded building works",
    note: "Lifts, shafts and pits are priced inside each building package.",
  },
  {
    id: "pitch",
    label: "Synthetic sports field",
    classification: "embedded",
    mapping: "Loaded site/outdoor works",
    note: "External sports fields package (1,385 m² synthetic field, retaining, fencing, lights).",
  },
  {
    id: "external-works",
    label: "External works",
    classification: "embedded",
    mapping: "Loaded site/outdoor works",
    note: "Pathways, landscaping, fencing, awnings, access road and external services package.",
  },
  {
    id: "parking",
    label: "Parking construction",
    classification: "embedded",
    mapping: "Loaded site/outdoor works",
    note: "New parking north of the senior school is priced inside external works.",
  },
  {
    id: "covered-play",
    label: "Covered play / walkway awnings",
    classification: "embedded",
    mapping: "Loaded site/outdoor works",
    note: "Four awning lines are priced inside external works; the glazed awning upgrade is an unpriced optional add-on.",
  },
  {
    id: "roof-upgrades",
    label: "Roof upgrades / repairs",
    classification: "excluded_tbc",
    mapping: "Upper bound only",
    note: "$1,347,145 shown outside the submitted total; retained in the upper bound at 5% margin. Vendor purchase-price recovery is unagreed.",
  },
  {
    id: "mechanical-infrastructure",
    label: "Mechanical infrastructure replacement",
    classification: "excluded_tbc",
    mapping: "Upper bound only",
    note: "$1,950,000 shown outside the submitted total; retained in the upper bound at 5% margin. Vendor purchase-price recovery is unagreed.",
  },
  {
    id: "av-cabling",
    label: "AV installation cabling",
    classification: "excluded_tbc",
    mapping: "No model cost",
    note: "$363,000 optional add-on outside both the submitted total and the upper bound.",
  },
  {
    id: "av-hardware",
    label: "AV hardware",
    classification: "separately_added",
    mapping: "Operator capex",
    note: "Source assumes all AV hardware is supplied by the client.",
  },
  {
    id: "gym-equipment",
    label: "Gym equipment",
    classification: "separately_added",
    mapping: "Operator capex",
    note: "Gym fitout is priced with equipment by others.",
  },
  {
    id: "operator-fitout",
    label: "Operator fitout",
    classification: "separately_added",
    mapping: "Operator capex",
    note: "Does not reduce or duplicate developer loaded works.",
  },
  {
    id: "signage",
    label: "Signage",
    classification: "excluded_tbc",
    mapping: "No model cost",
    note: "Not priced in RevG.",
  },
] as const;

/** The scope register that belongs to a loaded basis. No basis (legacy
 *  net-trade mode) has no register. A basis whose `sourceId` is undefined was
 *  saved before the field existed and is the Kurraba basis. An unknown id
 *  fails closed: no register rows rather than another preset's. */
export function scopeRegisterFor(
  basis: LoadedDeveloperCostBasis | undefined,
): readonly CostScopeRegisterItem[] {
  if (!basis) return [];
  switch (basis.sourceId) {
    case undefined:
    case KURRABA_SOURCE_ID:
      return KURRABA_SCOPE_REGISTER;
    case VERSATILE_REVG_SOURCE_ID:
      return VERSATILE_REVG_SCOPE_REGISTER;
    default:
      return [];
  }
}

/** Applies the Versatile RevG builder basis. Developer-side items that the
 *  builder revision does not change (consents, leverage, contribution terms)
 *  keep the Kurraba development-table values. The development management fee
 *  derives instead of being carried as an amount (Kyle, 2026-09-08): 4% of
 *  the model's fee base, which excludes land, holding interest, the line fee
 *  and construction interest by default. Kurraba's $2,744,021 is 3.95% of
 *  that base on the Waterloo file, so the two agree; Reddam's in-house copy
 *  ($4,244,021, 2.39% of works + fees + land) includes land and is not
 *  adopted. */
export const VERSATILE_REVG_DM_FEE_RATE = 0.04;

export function applyVersatileRevGCostPreset(source: Scenario): Scenario {
  const scenario = structuredClone(source);
  scenario.assumptions.developerCostBasis = structuredClone(
    VERSATILE_REVG_LOADED_COST_BASIS,
  );
  scenario.assumptions.developerCostAllowances = {
    contingency: {
      mode: "amount",
      amount: REVG_RAW_TOTAL * VERSATILE_REVG_LOADINGS.contingencyRate,
    },
    professionalFees: {
      mode: "amount",
      amount: REVG_RAW_TOTAL * VERSATILE_REVG_LOADINGS.consultantFeesRate,
    },
    developmentFee: { mode: "rate", rate: VERSATILE_REVG_DM_FEE_RATE },
    consentsAmount: 2_065_197,
  };
  scenario.assumptions.constructionLvrRate = 0.675;
  scenario.assumptions.reddamCapexContribution = 10_000_000;
  scenario.assumptions.contributionTiming = "progressive";
  return scenario;
}
